/* While a bottom sheet / modal overlay is open:
 *  - hide the floating bottom nav (on iOS Safari an element with backdrop-filter
 *    composites ABOVE later content regardless of z-index, so the nav punches
 *    through open sheets — hiding it is the reliable cross-platform fix);
 *  - tint the browser chrome to the sheet surface via theme-color (see below).
 *
 * Deliberately NO document-scroll freeze of any kind: a non-scrollable document
 * makes iOS Safari shrink the layout viewport, so fixed sheets stop reaching the
 * physical screen bottom and a grey chrome strip appears under them. Background
 * scroll bleed is prevented per-overlay with touch-action:none on scrims and
 * overscroll-behavior:contain on inner scrollers (_global.scss).
 *
 * Reference-counted so nested / quickly-swapped sheets don't unlock too early.
 */

let count = 0;

/* While a sheet is open, pin Safari's chrome tint to the sheet surface. With no
 * theme-color meta (the app's default — utils/theme.js removes it), iOS derives
 * the bar-zone colour from the page, i.e. from the scrim-dimmed menu behind the
 * sheet — a grey block right under the white popup (darker than the popup in
 * dark mode). Setting theme-color to --surface-card makes the zone around the
 * address pill match the sheet; removing it on close restores the derived tint.
 *
 * The value is re-asserted a few times after opening: iOS Safari can miss a
 * theme-color mutation that lands in the same frame as the sheet's open
 * animation (symptom on device: the bar only recoloured when a SECOND popup
 * opened on top and touched the meta again). Each write recreates the <meta>
 * node — a guaranteed DOM mutation Safari repaints from. */
let themeNudgeTimers = [];

function writeThemeColor(color) {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.remove();
  if (!color) return;
  const fresh = document.createElement('meta');
  fresh.setAttribute('name', 'theme-color');
  fresh.setAttribute('content', color);
  document.head.appendChild(fresh);
}

function setSheetThemeColor(on) {
  themeNudgeTimers.forEach(clearTimeout);
  themeNudgeTimers = [];
  if (on) {
    const surface = getComputedStyle(document.documentElement)
      .getPropertyValue('--surface-card').trim() || '#FFFFFF';
    writeThemeColor(surface);
    themeNudgeTimers = [250, 700].map(
      (ms) => setTimeout(() => writeThemeColor(surface), ms)
    );
  } else {
    writeThemeColor(null);
  }
}

function apply() {
  if (typeof document === 'undefined') return;
  const on = count > 0;
  document.body.classList.toggle('sm-sheet-open', on);
  setSheetThemeColor(on);

  if (!on) {
    // The dish-details sheet library (@webzlodimir/vue-bottom-sheet-vue2) inlines
    // overflow-y on <html> while open and leaves `overflow-y: auto` behind after
    // closing; the v1 library does the same with `overflow: hidden` on <body>.
    // Any explicit overflow there re-routes scrolling off the window: window
    // scroll events die (menu header stops collapsing) and iOS Safari pins its
    // toolbars open for good. The stylesheet neutralizes those inline styles
    // with !important (_global.scss); still, clear the leftovers on every
    // unlock so the DOM stays clean. Harmless when nothing set them.
    document.documentElement.style.overflowY = '';
    document.documentElement.style.overscrollBehavior = '';
    document.body.style.overflow = '';
  }
}

export function lockNav() {
  count += 1;
  apply();
}

export function unlockNav() {
  count = Math.max(0, count - 1);
  apply();
}

export default { lockNav, unlockNav };
